import React, { useEffect, useRef, useState } from 'react';

type HeroProps = {
  onDownloadClick?: React.MouseEventHandler<HTMLAnchorElement>;
};

const FACE_IMAGES = [
  '/api/faces_previews/face_8_no_logo_preview.png',
  '/api/faces_previews/face_17_no_logo_preview.png',
  '/api/faces_previews/face_14_no_logo_preview.png',
  '/api/faces_previews/face_5_no_logo_preview.png',
  '/api/faces_previews/face_10_no_logo_preview.png',
  '/api/faces_previews/face_11_no_logo_preview.png',
  '/api/faces_previews/face_12_no_logo_preview.png',
  '/api/faces_previews/face_13_no_logo_preview.png',
];

// Stats array
const STATS = [
  { value: '400+', label: 'Premium Faces' },
  { value: '50k+', label: 'Active Users' },
  { value: '4.9★', label: 'App Store Rating' },
];

const Hero: React.FC<HeroProps> = ({ onDownloadClick }) => {
  const [currentFace, setCurrentFace] = useState(0);
  const [visible, setVisible] = useState(true);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Cycle through faces
  useEffect(() => {
    intervalRef.current = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setCurrentFace(prev => (prev + 1) % FACE_IMAGES.length);
        setVisible(true);
      }, 400);
    }, 3000);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, []);

  return (
    <section
      className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden noise"
      style={{
        background: 'radial-gradient(ellipse 80% 60% at 50% 0%, rgba(104,193,174,0.12) 0%, transparent 70%), #080C0B',
      }}
    >
      {/* Ambient blobs */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div
          className="absolute animate-glow-pulse"
          style={{
            width: 700, height: 700,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(104,193,174,0.10) 0%, transparent 65%)',
            top: '-20%', left: '50%', transform: 'translateX(-50%)',
          }}
        />
        <div
          className="absolute"
          style={{
            width: 400, height: 400,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(104,193,174,0.08) 0%, transparent 65%)',
            bottom: '10%', right: '-10%',
          }}
        />
        {/* Subtle grid */}
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: 'linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)',
            backgroundSize: '60px 60px',
          }}
        />
      </div>

      <div className="container mx-auto relative z-10 pt-24 pb-16">
        <div className="grid md:grid-cols-2 gap-16 items-center">
          {/* Left — copy */}
          <div className="text-center md:text-left">
            {/* Badge */}
            <div className="inline-flex mb-6 animate-fade-in-up" style={{ animationDelay: '0ms' }}>
              <span className="section-badge">
                <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#68C1AE] animate-pulse" />
                New drops every week
              </span>
            </div>

            <h1
              className="text-5xl md:text-6xl lg:text-7xl font-extrabold leading-[1.06] tracking-tight animate-fade-in-up"
              style={{ animationDelay: '80ms', letterSpacing: '-0.03em' }}
            >
              Your wrist,{' '}
              <span className="gradient-text">reimagined</span>{' '}
              every day.
            </h1>

            <p
              className="mt-6 text-lg md:text-xl text-white/55 leading-relaxed max-w-lg animate-fade-in-up"
              style={{ animationDelay: '160ms' }}
            >
              Watchi brings hundreds of stunning Smart Watch faces, animated wallpapers, and smart widget stacks — curated by designers, loved by fans.
            </p>

            {/* CTA buttons */}
            <div className="mt-9 flex flex-col sm:flex-row gap-3 justify-center md:justify-start animate-fade-in-up" style={{ animationDelay: '240ms' }}>
              <a
                href="https://apps.apple.com/us/app/watchi-watch-faces-gallery/id6753596004"
                className="btn-primary"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
                </svg>
                Download Free
              </a>
              <a href="#gallery" className="btn-ghost">
                Browse Gallery
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </a>
            </div>

            {/* Stats */}
            <div className="mt-12 flex gap-8 justify-center md:justify-start animate-fade-in-up" style={{ animationDelay: '320ms' }}>
              {STATS.map((stat, i) => (
                <div key={i}>
                  <p className="text-2xl font-bold text-white tracking-tight">{stat.value}</p>
                  <p className="text-xs text-white/40 mt-0.5 font-medium">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Right — watch faces showcase */}
          <div className="relative flex justify-center items-center animate-fade-in" style={{ animationDelay: '200ms' }}>
            {/* Glow behind images */}
            <div
              className="absolute rounded-full animate-glow-pulse"
              style={{
                width: 360, height: 360,
                background: 'radial-gradient(circle, rgba(104,193,174,0.18) 0%, transparent 70%)',
                top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
              }}
            />

            {/* Main cycling face */}
            <div className="relative z-10 animate-float">
              <div
                className="relative overflow-hidden rounded-[36px] shadow-2xl ring-1 ring-white/10"
                style={{
                  width: 220,
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.06), rgba(255,255,255,0.02))',
                  boxShadow: '0 40px 120px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.08), inset 0 1px 0 rgba(255,255,255,0.1)',
                }}
              >
                <img
                  key={currentFace}
                  src={FACE_IMAGES[currentFace]}
                  alt="Watch face"
                  className="w-full transition-opacity duration-400"
                  style={{ opacity: visible ? 1 : 0, transition: 'opacity 0.4s ease' }}
                />
                {/* Shine overlay */}
                <div
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background: 'linear-gradient(135deg, rgba(255,255,255,0.08) 0%, transparent 50%)',
                    borderRadius: 36,
                  }}
                />
              </div>

              {/* Floating mini card */}
              <div
                className="absolute -bottom-5 -right-8 glass-card-light rounded-2xl px-4 py-3 shadow-xl"
                style={{ minWidth: 140 }}
              >
                <p className="text-[10px] uppercase tracking-widest text-white/40 font-semibold">Current vibe</p>
                <p className="text-sm font-bold text-white mt-1">Aurora Glow Face</p>
                <p className="text-[10px] text-[#68C1AE] mt-0.5 font-medium">★ 60k installs</p>
              </div>
            </div>

            {/* Secondary face — offset left */}
            <div className="absolute left-0 bottom-12 z-0 animate-float-delay hidden lg:block">
              <div
                className="overflow-hidden rounded-[28px] ring-1 ring-white/10"
                style={{
                  width: 130,
                  opacity: 0.7,
                  boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
                }}
              >
                <img src={FACE_IMAGES[(currentFace + 2) % FACE_IMAGES.length]} alt="Watch face alt" className="w-full" />
              </div>
            </div>

            {/* Trending badge */}
            <div
              className="absolute top-4 -left-4 glass-card-light rounded-2xl px-4 py-2.5 shadow-lg hidden lg:flex flex-col"
            >
              <span className="text-[10px] uppercase tracking-widest text-[#68C1AE] font-bold">🔥 Trending</span>
              <span className="text-xs text-white/70 mt-0.5">This week</span>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-fade-in" style={{ animationDelay: '500ms' }}>
        <span className="text-[11px] uppercase tracking-[0.15em] text-white/30 font-medium">Scroll to explore</span>
        <div className="w-5 h-8 rounded-full border border-white/20 flex justify-center pt-1.5">
          <div className="w-1 h-2 bg-white/40 rounded-full"
            style={{ animation: 'float 1.8s ease-in-out infinite' }}
          />
        </div>
      </div>
    </section>
  );
};

export default Hero;
